import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SPAN_SAVE_FOR_LATER } from '@constants/otel';
import { DepositOnly } from '@generated/gql/graphql';
import { useSnackbarErrors } from '@hooks/useSnackbarErrors';
import { useTrace } from '@services/otel';
import { BooleanSelection } from '@src/types/index';
import { useTrackingContext } from '@tracking/Provider/Context';
import { useTrackSignupOnequoteSaveSubmitted } from '@tracking/trackingEvents';

import useResetSaveForLaterRemoteForm from './SaveForm/hooks/useResetSaveForLaterRemoteForm';
import { saveForLaterMutation } from './query';
import { SaveFormValues } from './types';

const parseContacts = (contacts: string[]) =>
  contacts.every((contact) => !contact) ? undefined : contacts;

const useSaveForLaterForm = (basketId: string, partnerIDs?: string[]) => {
  const { withTrace } = useTrace();
  const navigate = useNavigate();
  const { trackingContexts } = useTrackingContext();
  const { enqueueSnackbar } = useSnackbar();
  const [submitErrors, setSubmitErrors] = useState<ErrorLike[]>([]);
  const [saveForLater, { loading }] = useMutation(saveForLaterMutation);
  const reset = useResetSaveForLaterRemoteForm();
  const trackSaveSubmitted = useTrackSignupOnequoteSaveSubmitted();

  useSnackbarErrors(submitErrors);

  const executeSaveForLater = async (values: SaveFormValues) => {
    const { mobiles, emails, acceptMarketing, sendEmailToPartners } = values;
    const isSendingToPartners = sendEmailToPartners === BooleanSelection.Yes;
    try {
      await saveForLater({
        variables: {
          details: {
            depositOnly: DepositOnly.NotDepositOnly,
            basketId,
            mobiles: parseContacts(mobiles),
            emails: parseContacts(emails),
            acceptedFollowUpComms: Boolean(
              acceptMarketing === BooleanSelection.Yes
            ),
            ...(isSendingToPartners && partnerIDs?.length
              ? { partnerIDs }
              : {}),
          },
        },
        onCompleted: () => {
          // or else the remote form will be populated with the previous values if the user goes back to the form
          reset();

          navigate(-1);
          trackSaveSubmitted(
            {
              email_entered: emails.filter(Boolean).length > 0,
              mobile_entered: mobiles.filter(Boolean).length > 0,
            },
            {
              contexts: trackingContexts,
            }
          );
          enqueueSnackbar('Link to your quote has been successfully sent!', {
            variant: 'success',
            autoHideDuration: 3000,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
          });
        },
      });
    } catch (error) {
      setSubmitErrors([error as ErrorLike]);
    }
  };

  const handleSubmit = (values: SaveFormValues) =>
    withTrace(SPAN_SAVE_FOR_LATER, () => executeSaveForLater(values));

  return { handleSubmit, isSaving: loading };
};

export { useSaveForLaterForm };
